/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { ColDef } from 'ag-grid-community';
import { numberAsStringComparator, threeDecimalValueFormatter } from 'components/common/Tables/CommonGridFormatters';

export enum FastestLapsTableColId {
  FastLapRank = 'fastLapRank',
  VehicleNumber = 'vehicleNumber',
  VehicleManufacturer = 'vehicleManufacturer',
  FastLap = 'fastLap',
  FastLapDiff = 'fastLapDiff',
  Fast10VehicleNumber = 'fast10VehicleNumber',
  Fast10VehicleManufacturer = 'fast10VehicleManufacturer',
  Fast10 = 'fast10',
  Fast10Diff = 'fast10Diff',
  Fast20VehicleNumber = 'fast20VehicleNumber',
  Fast20VehicleManufacturer = 'fast20VehicleManufacturer',
  Fast20 = 'fast20',
  Fast30VehicleNumber = 'fast30VehicleNumber',
  Fast30VehicleManufacturer = 'fast30VehicleManufacturer',
  Fast20Diff = 'fast20Diff',
  Fast30 = 'fast30',
  Fast30Diff = 'fast30Diff',
  FastXVehicleNumber = 'fastXVehicleNumber',
  FastXVehicleManufacturer = 'fastXVehicleManufacturer',
  FastX = 'fastX',
  FastXDiff = 'fastXDiff',
}

export const fastestLapsTableColDefs: ColDef[] = [
  {
    colId: FastestLapsTableColId.FastLapRank,
    headerName: 'Rank',
    flex: 1,
    headerTooltip: `Vehicle's rank for fastest lap`,
    type: 'numericColumn',
  },
  {
    colId: FastestLapsTableColId.VehicleNumber,
    headerName: 'Vehicle',
    flex: 1,
    headerTooltip: 'Vehicle Number',
    type: 'rightAligned',
    comparator: numberAsStringComparator,
  },
  {
    colId: FastestLapsTableColId.VehicleManufacturer,
    headerName: 'Make',
    flex: 1,
    headerTooltip: 'Vehicle Make',
    type: 'rightAligned',
  },
  {
    colId: FastestLapsTableColId.FastLap,
    headerName: 'Fast Lap',
    flex: 2,
    headerTooltip: `Vehicle's fastest single lap`,
    type: 'numericColumn',
    valueFormatter: threeDecimalValueFormatter,
  },
  {
    colId: FastestLapsTableColId.FastLapDiff,
    headerName: 'Diff',
    flex: 1,
    type: 'rightAligned',
    headerTooltip: `Difference between this vehicle's previous fastest column (Fast Lap) and the leader's`,
    valueFormatter: threeDecimalValueFormatter,
  },
  {
    colId: FastestLapsTableColId.Fast10VehicleNumber,
    headerName: 'Vehicle',
    flex: 1,
    headerTooltip: 'Vehicle Number',
    type: 'rightAligned',
    comparator: numberAsStringComparator,
  },
  {
    colId: FastestLapsTableColId.Fast10VehicleManufacturer,
    headerName: 'Make',
    flex: 1,
    headerTooltip: 'Vehicle Make',
    type: 'rightAligned',
  },
  {
    colId: FastestLapsTableColId.Fast10,
    headerName: 'Fast 10',
    flex: 1,
    headerTooltip: `Vehicle's fastest 10 laps`,
    type: 'numericColumn',
    valueFormatter: threeDecimalValueFormatter,
  },
  {
    colId: FastestLapsTableColId.Fast10Diff,
    headerName: 'Diff',
    flex: 1,
    headerTooltip: `Difference between this vehicle's previous fastest column (Fast 10) and the leader's`,
    type: 'numericColumn',
    valueFormatter: threeDecimalValueFormatter,
  },
  {
    colId: FastestLapsTableColId.Fast20VehicleNumber,
    headerName: 'Vehicle',
    flex: 1,
    headerTooltip: 'Vehicle Number',
    type: 'rightAligned',
    comparator: numberAsStringComparator,
  },
  {
    colId: FastestLapsTableColId.Fast20VehicleManufacturer,
    headerName: 'Make',
    flex: 1,
    headerTooltip: 'Vehicle Make',
    type: 'rightAligned',
  },
  {
    colId: FastestLapsTableColId.Fast20,
    headerName: 'Fast 20',
    flex: 1,
    headerTooltip: `Vehicle's fastest 20 laps`,
    type: 'numericColumn',
    valueFormatter: threeDecimalValueFormatter,
  },
  {
    colId: FastestLapsTableColId.Fast20Diff,
    headerName: 'Diff',
    flex: 1,
    headerTooltip: `Difference between this vehicle's previous fastest column (Fast 20) and the leader's`,
    type: 'numericColumn',
    valueFormatter: threeDecimalValueFormatter,
  },
  {
    colId: FastestLapsTableColId.Fast30VehicleNumber,
    headerName: 'Vehicle',
    flex: 1,
    headerTooltip: 'Vehicle Number',
    type: 'rightAligned',
    comparator: numberAsStringComparator,
  },
  {
    colId: FastestLapsTableColId.Fast30VehicleManufacturer,
    headerName: 'Make',
    flex: 1,
    headerTooltip: 'Vehicle Make',
    type: 'rightAligned',
  },
  {
    colId: FastestLapsTableColId.Fast30,
    headerName: 'Fast 30',
    flex: 1,
    headerTooltip: `Vehicle's fastest 30 laps`,
    type: 'numericColumn',
    valueFormatter: threeDecimalValueFormatter,
  },
  {
    colId: FastestLapsTableColId.Fast30Diff,
    headerName: 'Diff',
    flex: 1,
    headerTooltip: `Difference between this vehicle's previous fastest column (Fast 30) and the leader's`,
    type: 'numericColumn',
    valueFormatter: threeDecimalValueFormatter,
  },
  {
    colId: FastestLapsTableColId.FastXVehicleNumber,
    headerName: 'Vehicle',
    flex: 1,
    headerTooltip: 'Vehicle Number',
    type: 'rightAligned',
    comparator: numberAsStringComparator,
  },
  {
    colId: FastestLapsTableColId.FastXVehicleManufacturer,
    headerName: 'Make',
    flex: 1,
    headerTooltip: 'Vehicle Make',
    type: 'rightAligned',
  },
  {
    colId: FastestLapsTableColId.FastX,
    headerName: 'Fast X',
    flex: 1,
    headerTooltip: `Vehicle's fastest X laps`,
    type: 'numericColumn',
    valueFormatter: threeDecimalValueFormatter,
  },
  {
    colId: FastestLapsTableColId.FastXDiff,
    headerName: 'Diff',
    flex: 1,
    headerTooltip: `Difference between this vehicle's previous fastest column (Fast X) and the leader's`,
    type: 'numericColumn',
    valueFormatter: threeDecimalValueFormatter,
  },
];

export const fastLapFields: string[] = [
  FastestLapsTableColId.VehicleNumber,
  FastestLapsTableColId.VehicleManufacturer,
  FastestLapsTableColId.FastLap,
  FastestLapsTableColId.FastLapDiff,
];
export const fast10LapFields: string[] = [
  FastestLapsTableColId.Fast10VehicleNumber,
  FastestLapsTableColId.Fast10VehicleManufacturer,
  FastestLapsTableColId.Fast10,
  FastestLapsTableColId.Fast10Diff,
];
export const fast20LapFields: string[] = [
  FastestLapsTableColId.Fast20VehicleNumber,
  FastestLapsTableColId.Fast20VehicleManufacturer,
  FastestLapsTableColId.Fast20,
  FastestLapsTableColId.Fast20Diff,
];
export const fast30LapFields: string[] = [
  FastestLapsTableColId.Fast30VehicleNumber,
  FastestLapsTableColId.Fast30VehicleManufacturer,
  FastestLapsTableColId.Fast30,
  FastestLapsTableColId.Fast30Diff,
];
export const fastXLapFields: string[] = [
  FastestLapsTableColId.FastXVehicleNumber,
  FastestLapsTableColId.FastXVehicleManufacturer,
  FastestLapsTableColId.FastX,
  FastestLapsTableColId.FastXDiff,
];
